import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { Image } from "@nykaa/payment-ui/src/Image";
import { Title } from "@nykaa/payment-ui/src/Title";
import { BottomSheet } from "@nykaa/payment-ui/src/BottomSheet/BottomSheet";
import { SubTitle } from "@nykaa/payment-ui/src/SubTitle";

import { AddressUpiTimerScreen } from "./AddressUpiTimerScreen";
import CashOnDeliveryWrapper from "layouts/Payment/PaymentMethods/CashOnDeliveryWrapper";
import UpiMethodWrapper from "layouts/Payment/PaymentMethods/UpiMethodWrapper";

import {
  PaymentRecommendations,
  PaymentRecommendationsHeader,
  RecommendationsOption,
  OtherPaymentOptionWrapper,
  BottomSheetRecommendationsHeader,
  OtherOption,
  TitelWrapper,
  RecommendationsWrapper,
} from "../assets/style";

import { getEndIconForPaymentFailure, getIcons, ICONS_NAMES } from "helpers/utils/getIcons";
import { getOtherPaymentOptionConfig } from "../helper";
import { PAYMENT_MODES } from "constants/paymentModes";
import { trackPaymentMethodClick } from "layouts/Payment/common/dependencies/tracking";
import { BUY_NOW_FLOW_QUERY_PARAM, DEVICE_TYPE, PAGE_NAMES } from "../../../constants";
import { CLIENT_URLS } from "constants/urls";
import { formatNumber } from "helpers/utils/numberFormatter";
import { appendQueryStringToUrl } from "helpers/utils/url";
import { shouldAppendBuyNowApiParam } from "helpers/utils/buyNowCheckoutFlow";


export const PaymentOptionManager = ({
  cartInfo,
  upiTimerConfig,
  savingText,
  codConfig,
  upiConfig,
  paymentOptionConfig,
  orderAmount,
  paymentMethodSheetVisible,
  togglePaymentMethodSheet,
  isGuest,
  theme,
}: IPaymentOptionManager) => {
  const history = useHistory();
  const contentRefs = useRef(null as HTMLElement | null);
  const isCodAvailable = cartInfo?.isCodAvailable;
  const [currentPaymentOption, setCurrentPaymentOption] = useState(paymentOptionConfig.mode);
  const otherPaymentMethodConfig = getOtherPaymentOptionConfig(history, isGuest);

  const handleChangePaymentOption = (option: string) => {
    /**
     * track payment option selection.
     */
    trackPaymentMethodClick({
      paymentMethod: option,
      isSaved: false,
      deviceType: DEVICE_TYPE.MOBILE,
      isLoggedIn: !isGuest,
      interactionLocation: PAGE_NAMES.PAYMENT_BOTTOMSHEET,
    });
    setCurrentPaymentOption(option);
  };

  /**
   * maintaing the view for upi timer screen.
   */
  useEffect(() => {
    if (upiTimerConfig.isVisible) {
        contentRefs?.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
    }
  }, [upiTimerConfig.isVisible]);

  useEffect(() => {
    if (!isCodAvailable) {
      setCurrentPaymentOption(PAYMENT_MODES.UPI);
    } else {
      setCurrentPaymentOption(paymentOptionConfig.mode);
    }
  }, [isCodAvailable]);

  return (
    <BottomSheet
      isVisible={paymentMethodSheetVisible}
      leadingIcon={!upiTimerConfig.isVisible ? getIcons("", ICONS_NAMES.CLEAR) : ""} // for upi polling screen not showing close icon
      onIconClick={() => {
        let redirectionUrl = CLIENT_URLS.PAYMENT_BASE_URL;
        if (shouldAppendBuyNowApiParam()) {
          redirectionUrl = appendQueryStringToUrl(redirectionUrl, `${BUY_NOW_FLOW_QUERY_PARAM}=true`);
        }
        history.replace(redirectionUrl);
      }}
      theme={theme}
      height={"85%"}
      zIndex={2}
    >
      {upiTimerConfig.isVisible ? (
        <div ref={el => (contentRefs.current = el)}>
          <AddressUpiTimerScreen upiTimerConfig={upiTimerConfig} theme={theme} />
        </div>
      ) : (
        <>
          <BottomSheetRecommendationsHeader>
            <Title
              text={`Pay ₹${formatNumber(orderAmount)}`}
              font={theme.typography.titleXLarge}
              color={theme.colors.textPrimary}
            />
            <SubTitle
              htmlText={savingText}
              font={theme.typography.subTitleSmall}
              theme={theme}
              color={theme.colors.textSecondary}
            />
          </BottomSheetRecommendationsHeader>
          {/* Recommended payment section */}
          <RecommendationsWrapper>
            <PaymentRecommendations>
              <PaymentRecommendationsHeader>
                <Image
                  url={getIcons("", ICONS_NAMES.STAR_ICON)}
                  alt={"recommendation"}
                  theme={theme}
                />
                <Title
                  text={"Recommended"}
                  font={theme.typography.subTitleMedium}
                  color={theme.colors.info}
                />
              </PaymentRecommendationsHeader>
              <RecommendationsOption>
                <div onClick={() => handleChangePaymentOption(PAYMENT_MODES.COD)}>
                  <CashOnDeliveryWrapper
                    {...{
                      ...codConfig,
                      isAccordianOpened: currentPaymentOption === PAYMENT_MODES.COD,
                      endIcon: getEndIconForPaymentFailure(
                        currentPaymentOption === PAYMENT_MODES.COD
                      ),
                      disableNudge: true,
                    }}
                  />
                </div>
                <div onClick={() => handleChangePaymentOption(PAYMENT_MODES.UPI)}>
                  <UpiMethodWrapper
                    {...{
                      ...upiConfig,
                      hideHeader: false,
                      isBackGroundOverflow: true, // to avoid background scroll
                      isAccordianOpened: currentPaymentOption === PAYMENT_MODES.UPI,
                      endIcon: getEndIconForPaymentFailure(
                        currentPaymentOption === PAYMENT_MODES.UPI
                      ),
                      textIcon: getIcons("", ICONS_NAMES.INSTANT_ICON),
                    }}
                  />
                </div>
              </RecommendationsOption>
            </PaymentRecommendations>
            <OtherPaymentOptionWrapper>
              {otherPaymentMethodConfig.map(option => {
                return (
                  <OtherOption onClick={option.clickHandler}>
                    <TitelWrapper>
                      <Image url={option.leadIcon} alt={"Icon"} theme={theme} />
                      <Title
                        text={option.label}
                        font={theme.typography.subTitleLarge}
                        color={theme.colors.textPrimary}
                      />
                    </TitelWrapper>
                    <Image url={option.endIcon} alt={"Icon"} theme={theme} />
                  </OtherOption>
                );
              })}
            </OtherPaymentOptionWrapper>
          </RecommendationsWrapper>
        </>
      )}
    </BottomSheet>
  );
};
